/**
 * EarlyPaymentModal
 * Modal that handles the form for all 3 early payment types.
 * Settlement shows a quote breakdown (capital + accrued interest + IVA) before confirmation.
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EarlyPaymentResult } from '@/features/credit/components/EarlyPaymentResult';
import { formatCurrency } from '@/utils/formatters';
import type { EarlyPaymentType, EarlyPaymentResponse, SettlementQuoteResponse } from '@/types/early-payment.types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EarlyPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentType: EarlyPaymentType | null;
  creditId: string;
  outstandingBalance: number;
  weeklyPayment: number;
  onSubmit: (amount?: number) => void;
  isPending: boolean;
  isSuccess: boolean;
  lastResult: EarlyPaymentResponse | null;
  settlementQuote: SettlementQuoteResponse | null;
  isLoadingQuote: boolean;
}

// ─── Config per payment type ─────────────────────────────────────────────────

interface PaymentTypeConfig {
  title: string;
  hint: string;
  submitLabel: string;
  requiresAmount: boolean;
}

const PAYMENT_CONFIG: Record<EarlyPaymentType, PaymentTypeConfig> = {
  ANTICIPATED: {
    title: 'Abono a Capital',
    hint: 'El monto se aplicara al capital. Los pagos semanales se recalcularan.',
    submitLabel: 'Aplicar Abono',
    requiresAmount: true,
  },
  ADVANCED: {
    title: 'Pago Adelantado',
    hint: 'Se pagaran los proximos cupones en orden cronologico.',
    submitLabel: 'Aplicar Pago',
    requiresAmount: true,
  },
  SETTLEMENT: {
    title: 'Liquidar Credito',
    hint: 'Se cobrara el capital pendiente mas los intereses devengados hasta hoy.',
    submitLabel: 'Confirmar Liquidacion',
    requiresAmount: false,
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function EarlyPaymentModal({
  isOpen,
  onClose,
  paymentType,
  creditId,
  outstandingBalance,
  weeklyPayment,
  onSubmit,
  isPending,
  isSuccess,
  lastResult,
  settlementQuote,
  isLoadingQuote,
}: EarlyPaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  // Reset form state when modal opens/closes or payment type changes
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setError('');
      setConfirmed(false);
    }
  }, [isOpen, paymentType]);

  if (!paymentType) return null;

  const config = PAYMENT_CONFIG[paymentType];

  // ─── Validation ──────────────────────────────────────────────────────────

  function validate(): boolean {
    if (!config.requiresAmount) return true;

    const trimmed = amount.trim();
    if (!trimmed) {
      setError('Ingresa un monto');
      return false;
    }

    const parsed = parseFloat(trimmed);
    if (isNaN(parsed) || parsed <= 0) {
      setError('El monto debe ser mayor a $0');
      return false;
    }

    // Check max 2 decimals
    const parts = trimmed.split('.');
    if (parts[1] && parts[1].length > 2) {
      setError('El monto no puede tener mas de 2 decimales');
      return false;
    }

    if (parsed > outstandingBalance) {
      setError(`El monto no puede exceder el saldo pendiente (${formatCurrency(outstandingBalance, 'MXN', 'es-MX')})`);
      return false;
    }

    setError('');
    return true;
  }

  function handleSubmit() {
    if (config.requiresAmount) {
      if (!validate()) return;
      const parsed = parseFloat(parseFloat(amount).toFixed(2));
      onSubmit(parsed);
    } else {
      // Settlement: requires confirmation checkbox
      if (!confirmed) {
        setError('Debes confirmar para continuar');
        return;
      }
      setError('');
      onSubmit();
    }
  }

  // ─── Result view ─────────────────────────────────────────────────────────

  if (isSuccess && lastResult) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={config.title}>
        <EarlyPaymentResult
          result={lastResult}
          paymentType={paymentType}
          onClose={onClose}
        />
      </Modal>
    );
  }

  // ─── Settlement Quote view ────────────────────────────────────────────────

  if (paymentType === 'SETTLEMENT') {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={config.title}>
        <div className="space-y-4">
          {isLoadingQuote ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="ml-2 text-sm text-muted-foreground">Calculando liquidacion...</span>
            </div>
          ) : settlementQuote ? (
            <>
              {/* Quote breakdown */}
              <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Capital Pendiente</span>
                  <span className="text-sm font-semibold tabular-nums text-foreground">
                    {formatCurrency(settlementQuote.outstanding_principal_mxn, 'MXN', 'es-MX')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Intereses Devengados</span>
                  <span className="text-sm tabular-nums text-foreground">
                    {formatCurrency(settlementQuote.accrued_interest_mxn, 'MXN', 'es-MX')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">IVA</span>
                  <span className="text-sm tabular-nums text-foreground">
                    {formatCurrency(settlementQuote.accrued_iva_mxn, 'MXN', 'es-MX')}
                  </span>
                </div>
                <div className="border-t border-border pt-2 flex justify-between">
                  <span className="text-sm font-semibold text-foreground">Total a Liquidar</span>
                  <span className="text-base font-bold tabular-nums text-foreground">
                    {formatCurrency(settlementQuote.total_settlement_mxn, 'MXN', 'es-MX')}
                  </span>
                </div>
                {settlementQuote.interest_saved_mxn > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-green-600">Ahorro en Intereses</span>
                    <span className="text-sm font-medium tabular-nums text-green-600">
                      {formatCurrency(settlementQuote.interest_saved_mxn, 'MXN', 'es-MX')}
                    </span>
                  </div>
                )}
              </div>

              {/* Balance check */}
              <div className={`rounded-lg border p-3 ${
                settlementQuote.has_sufficient_funds
                  ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/50'
                  : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/50'
              }`}>
                <p className={`text-sm ${
                  settlementQuote.has_sufficient_funds
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {settlementQuote.has_sufficient_funds
                    ? `Saldo disponible: ${formatCurrency(settlementQuote.available_balance_mxn, 'MXN', 'es-MX')} - Fondos suficientes`
                    : `Saldo disponible: ${formatCurrency(settlementQuote.available_balance_mxn, 'MXN', 'es-MX')} - Fondos insuficientes`
                  }
                </p>
              </div>

              {/* Confirmation checkbox - only if funds available */}
              {settlementQuote.has_sufficient_funds && (
                <>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => {
                        setConfirmed(e.target.checked);
                        if (error) setError('');
                      }}
                      disabled={isPending}
                      className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">
                      Confirmo que deseo liquidar este credito por {formatCurrency(settlementQuote.total_settlement_mxn, 'MXN', 'es-MX')}
                    </span>
                  </label>

                  {error && (
                    <p className="text-sm text-red-500" role="alert">
                      {error}
                    </p>
                  )}
                </>
              )}

              {/* Credit ID reference */}
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>ID Credito</span>
                <span className="font-mono">{creditId.slice(0, 12)}...</span>
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
              <p className="text-sm text-red-800 dark:text-red-200">
                No se pudo obtener la cotizacion de liquidacion.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              Cancelar
            </Button>
            {settlementQuote?.has_sufficient_funds && (
              <Button
                variant="danger"
                onClick={handleSubmit}
                isLoading={isPending}
                disabled={isPending || !confirmed}
              >
                {config.submitLabel}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // ─── Form view (ANTICIPATED / ADVANCED) ─────────────────────────────────

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={config.title}>
      <div className="space-y-4">
        {/* Balance reference */}
        <div className="rounded-lg border border-border bg-muted/50 p-3">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Saldo Pendiente</span>
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {formatCurrency(outstandingBalance, 'MXN', 'es-MX')}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-sm text-muted-foreground">Pago Semanal</span>
            <span className="text-sm tabular-nums text-foreground">
              {formatCurrency(weeklyPayment, 'MXN', 'es-MX')}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-sm text-muted-foreground">ID Credito</span>
            <span className="text-xs font-mono text-muted-foreground">
              {creditId.slice(0, 12)}...
            </span>
          </div>
        </div>

        {/* Amount input for ANTICIPATED and ADVANCED */}
        <Input
          label="Monto (MXN)"
          type="number"
          min="0.01"
          max={outstandingBalance}
          step="0.01"
          placeholder="0.00"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            if (error) setError('');
          }}
          error={error}
          hint={config.hint}
          disabled={isPending}
          required
        />

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isPending}
            disabled={isPending}
          >
            {config.submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
